import { Loader2 } from 'lucide-react';
import { useTranslation } from '../../i18n';

interface SnippetPreviewPanelProps {
  /** Resolved text to show; empty shows a muted placeholder. */
  text: string;
  loading?: boolean;
  /** Kicker label; defaults to "Vorschau". */
  label?: string;
  /** Muted explanation under the text (e.g. "with sample data"). */
  hint?: string;
  className?: string;
}

/**
 * Flat read-only panel that shows a rendered snippet text under a text field or in the
 * snippet editor. Announces updates politely so live previews do not interrupt typing.
 */
export function SnippetPreviewPanel({
  text,
  loading = false,
  label,
  hint,
  className = '',
}: SnippetPreviewPanelProps) {
  const { t } = useTranslation(['textSnippets']);
  const isEmpty = text.trim().length === 0;

  return (
    <div
      className={`rounded-md border border-slate-200 bg-slate-50 px-3 py-2 ${className}`}
      aria-live="polite"
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
          {label ?? t('textSnippets:preview.title')}
        </span>
        {loading && (
          <Loader2
            className="w-3.5 h-3.5 animate-spin text-slate-400"
            strokeWidth={2}
            aria-hidden="true"
          />
        )}
      </div>
      <div className="max-h-40 overflow-y-auto">
        {isEmpty ? (
          <p className="text-[13px] text-slate-400">{t('textSnippets:preview.empty')}</p>
        ) : (
          <p className="text-[13px] text-slate-700 whitespace-pre-wrap break-words">{text}</p>
        )}
      </div>
      {hint && <p className="text-[11px] text-slate-500 mt-1.5">{hint}</p>}
    </div>
  );
}

export default SnippetPreviewPanel;
